import React from 'react';
import { Alert, Card, Typography, Form, Input, Select, DatePicker, Space, message, Row, Col } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { insertData, fetchSqlDataWithQuery, updateData } from '../../../services/api';
import { useGetUnlocodeCountries } from '../../../hooks/genericHooks';
import type { MatLogisticShipment } from '../../../types';
import FileUploadTool, { FileUploadToolHandle } from '../../../components/common/FileUploadTool';
import FileDownloadTool from '../../../components/common/FileDownloadTool';
import Button from '../../../components/common/Button';

const { Title } = Typography;

type TransportType = 'sea' | 'air' | 'road' | 'rail' | 'other' | 'tbd';

type ShipmentType = 'direct' | 'transit';

const transportTypeOptions: TransportType[] = ['sea', 'air', 'road', 'rail', 'other', 'tbd'];

const shipmentTypeOptions: ShipmentType[] = ['direct', 'transit'];

export type MatLogShipmentMode = 'create' | 'edit';

export type MatLogShipmentUpsertProps = {
  mode?: MatLogShipmentMode;
  initialShipment?: MatLogisticShipment | null;
  onSuccess?: (shipmentId: string) => void;
  readOnly?: boolean;
};

type FormValues = {
  shipment_id: string;
  shipment_type: ShipmentType;
  transport_type: TransportType;
  po_number?: string | null;
  invoice_no?: string | null;
  departure_country_code: string;
  departure_loc_code: string;
  departure_port?: string | null;
  destination_country_code: string;
  destination_loc_code: string;
  destination_port?: string | null;
  planned_date_range?: [Dayjs | null, Dayjs | null] | undefined;
  actual_departure_date?: Dayjs | null;
  actual_arrival_date?: Dayjs | null;
  forwarder_name?: string | null;
  carrier_name?: string | null;
  vessel_name?: string | null;
  container_no?: string | null;
  airwaybill_no?: string | null;
  billofLading_no?: string | null;
  plate_no1?: string | null;
  plate_no2?: string | null;
  vagon_no?: string | null;
};

export const MatLogShipmentUpsert: React.FC<MatLogShipmentUpsertProps> = ({
  mode = 'create',
  initialShipment = null,
  onSuccess,
  readOnly = false,
}) => {
  const itemStyle: React.CSSProperties = { marginBottom: 6 };
  const headerItemProps = React.useMemo(
    () => ({
      labelCol: { span: 24, style: { paddingBottom: 0, lineHeight: 1 } },
      wrapperCol: { span: 24, style: { paddingTop: 0, marginTop: -2 } },
    }),
    []
  );
  const toUpperOrSame = React.useCallback((v: any) => {
    if (typeof v !== 'string') return v;
    return v.toUpperCase();
  }, []);

  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = React.useState(false);
  const hydratingRef = React.useRef(false);
  const fileUploadRef = React.useRef<FileUploadToolHandle>(null);
  const [uploadPath, setUploadPath] = React.useState<string>(() => String((initialShipment as any)?.upload_path || ''));
  const prevDepCountryRef = React.useRef<string | undefined>(undefined);
  const prevDestCountryRef = React.useRef<string | undefined>(undefined);
  const prevTransportTypeRef = React.useRef<string | undefined>(undefined);
  const depPortTouchedRef = React.useRef(false);
  const destPortTouchedRef = React.useRef(false);

  // Countries (persisted, small)
  const { data: countriesData } = useGetUnlocodeCountries();
  const countryOptions = React.useMemo(() => {
    const rows = (countriesData?.data ?? countriesData ?? []) as Array<any>;
    return rows
      .filter((r) => r.country_code && r.country_name)
      .map((r) => ({ label: `${r.country_name} (${r.country_code})`, value: r.country_code }));
  }, [countriesData]);

  // Dynamic location options (server filtered via SQL query)
  const [depLocOptions, setDepLocOptions] = React.useState<Array<{ label: string; value: string }>>([]);
  const [destLocOptions, setDestLocOptions] = React.useState<Array<{ label: string; value: string }>>([]);
  const [depSearch, setDepSearch] = React.useState<string>('');
  const [destSearch, setDestSearch] = React.useState<string>('');
  const [loadingDep, setLoadingDep] = React.useState(false);
  const [loadingDest, setLoadingDest] = React.useState(false);

  const clearDepSearch = React.useCallback(() => setDepSearch(''), []);
  const clearDestSearch = React.useCallback(() => setDestSearch(''), []);

  const onClearDepartureCountry = React.useCallback(() => {
    clearDepSearch();
    setDepLocOptions([]);
    form.setFieldValue('departure_country_code', undefined);
    form.setFieldValue('departure_loc_code', undefined);
  }, [clearDepSearch, form]);

  const onClearDestinationCountry = React.useCallback(() => {
    clearDestSearch();
    setDestLocOptions([]);
    form.setFieldValue('destination_country_code', undefined);
    form.setFieldValue('destination_loc_code', undefined);
  }, [clearDestSearch, form]);

  const onClearDepartureLocation = React.useCallback(() => {
    clearDepSearch();
    form.setFieldValue('departure_loc_code', undefined);
  }, [clearDepSearch, form]);

  const onClearDestinationLocation = React.useCallback(() => {
    clearDestSearch();
    form.setFieldValue('destination_loc_code', undefined);
  }, [clearDestSearch, form]);

  const getFlagCondition = (tt?: string) => {
    switch ((tt || '').toLowerCase()) {
      case 'sea':
        return 'has_port = true';
      case 'air':
        return 'has_airport = true';
      case 'road':
        return 'has_road = true';
      case 'rail':
        return 'has_rail = true';
      default:
        return '1=1';
    }
  };

  const fetchLocationOptionByCode = React.useCallback(async (locationCode: string, countryCode?: string | null) => {
    const code = String(locationCode || '').trim();
    if (!code) return null;
    const safe = code.replace(/'/g, "''");
    const cc = countryCode ? String(countryCode).trim() : '';
    const safeCc = cc ? cc.replace(/'/g, "''") : '';

    const runQuery = async (sql: string) => {
      const res = await fetchSqlDataWithQuery(sql);
      const rows = Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);
      return rows && rows[0] ? rows[0] : null;
    };

    // Prefer (country_code + location_code) if provided; fallback to code-only.
    const primarySql = safeCc
      ? `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where country_code = '${safeCc}'
          and location_code = '${safe}'
        limit 1`
      : `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where location_code = '${safe}'
        limit 1`;

    let r = await runQuery(primarySql);
    if (!r && safeCc) {
      const fallbackSql = `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where location_code = '${safe}'
        limit 1`;
      r = await runQuery(fallbackSql);
    }
    if (!r?.location_code) return null;
    return {
      label: `${r.loc_name ?? ''} (${r.location_code})`,
      value: String(r.location_code),
    } as { label: string; value: string };
  }, []);

  const fetchLocationNameByCode = React.useCallback(async (locationCode: string, countryCode?: string | null) => {
    const code = String(locationCode || '').trim();
    if (!code) return null;
    const safe = code.replace(/'/g, "''");
    const cc = countryCode ? String(countryCode).trim() : '';
    const safeCc = cc ? cc.replace(/'/g, "''") : '';

    const runQuery = async (sql: string) => {
      const res = await fetchSqlDataWithQuery(sql);
      const rows = Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);
      return rows && rows[0] ? rows[0] : null;
    };

    const primarySql = safeCc
      ? `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where country_code = '${safeCc}'
          and location_code = '${safe}'
        limit 1`
      : `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where location_code = '${safe}'
        limit 1`;

    let r = await runQuery(primarySql);
    if (!r && safeCc) {
      const fallbackSql = `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where location_code = '${safe}'
        limit 1`;
      r = await runQuery(fallbackSql);
    }
    const name = String(r?.loc_name ?? '').trim();
    return name ? name : null;
  }, []);

  const mergeOptionFirst = React.useCallback((opt: { label: string; value: string } | null, opts: Array<{ label: string; value: string }>) => {
    if (!opt) return opts;
    const rest = (opts || []).filter((x) => String(x.value) !== String(opt.value));
    return [opt, ...rest];
  }, []);

  const fetchLocations = React.useCallback(
    async (
      countryCode: string | undefined,
      transportType: string | undefined,
      term: string | undefined,
      selectedCode: string | undefined,
      setter: (opts: any[]) => void,
      setLoading: (v: boolean) => void
    ) => {
      if (!countryCode) { setter([]); return; }
      const nameCond = term && term.trim() !== '' ? `and loc_name ilike '%${term.replace(/'/g, "''").trim()}%'` : '';
      const sql = `
        select location_code, loc_name
        from public.mv_cor_unece_unlocodes_basic
        where country_code = '${countryCode.replace(/'/g, "''")}'
          and ${getFlagCondition(transportType)}
          ${nameCond}
        order by loc_name
        limit 200`;
      try {
        setLoading(true);
        const res = await fetchSqlDataWithQuery(sql);
        const rows = Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);
        const base = rows.map((r: any) => ({ label: `${r.loc_name ?? ''} (${r.location_code})`, value: String(r.location_code) })) as Array<{ label: string; value: string }>;
        const selectedMissing = !!selectedCode && !base.some((x) => String(x.value) === String(selectedCode));
        const selectedOpt = selectedMissing ? await fetchLocationOptionByCode(String(selectedCode), countryCode) : null;
        setter(mergeOptionFirst(selectedOpt, base));
      } catch (e: any) {
        message.error(e?.message || 'Failed to load locations');
        setter([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchLocationOptionByCode, mergeOptionFirst]
  );

  // Watchers
  const depCountry = Form.useWatch('departure_country_code', form) as string | undefined;
  const destCountry = Form.useWatch('destination_country_code', form) as string | undefined;
  const transportType = Form.useWatch('transport_type', form) as string | undefined;
  const depLocCode = Form.useWatch('departure_loc_code', form) as string | undefined;
  const destLocCode = Form.useWatch('destination_loc_code', form) as string | undefined;

  React.useEffect(() => {
    const selected = form.getFieldValue('departure_loc_code') as string | undefined;
    fetchLocations(depCountry, transportType, depSearch, selected, setDepLocOptions, setLoadingDep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depCountry, transportType, depSearch]);
  React.useEffect(() => {
    const selected = form.getFieldValue('destination_loc_code') as string | undefined;
    fetchLocations(destCountry, transportType, destSearch, selected, setDestLocOptions, setLoadingDest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destCountry, transportType, destSearch]);

  React.useEffect(() => {
    const prevDep = prevDepCountryRef.current;
    const prevT = prevTransportTypeRef.current;
    const isFirst = prevDep === undefined && prevT === undefined;
    const changed = depCountry !== prevDep || transportType !== prevT;

    prevDepCountryRef.current = depCountry;
    prevTransportTypeRef.current = transportType;

    if (hydratingRef.current) return;
    if (isFirst) return;
    if (!changed) return;

    clearDepSearch();
    form.setFieldValue('departure_loc_code', undefined);
  }, [depCountry, transportType, clearDepSearch, form]);

  React.useEffect(() => {
    if (hydratingRef.current) return;
    if (readOnly) return;
    if (!depLocCode) return;
    if (depPortTouchedRef.current) return;

    let cancelled = false;
    void (async () => {
      const name = await fetchLocationNameByCode(String(depLocCode), depCountry ?? null);
      if (cancelled) return;
      if (!name) return;
      form.setFieldValue('departure_port', name);
    })();
    return () => {
      cancelled = true;
    };
  }, [depCountry, depLocCode, fetchLocationNameByCode, form, readOnly]);

  React.useEffect(() => {
    const prevDest = prevDestCountryRef.current;
    const prevT = prevTransportTypeRef.current;
    const isFirst = prevDest === undefined && prevT === undefined;
    const changed = destCountry !== prevDest || transportType !== prevT;

    prevDestCountryRef.current = destCountry;
    prevTransportTypeRef.current = transportType;

    if (hydratingRef.current) return;
    if (isFirst) return;
    if (!changed) return;

    clearDestSearch();
    form.setFieldValue('destination_loc_code', undefined);
  }, [destCountry, transportType, clearDestSearch, form]);

  React.useEffect(() => {
    if (hydratingRef.current) return;
    if (readOnly) return;
    if (!destLocCode) return;
    if (destPortTouchedRef.current) return;

    let cancelled = false;
    void (async () => {
      const name = await fetchLocationNameByCode(String(destLocCode), destCountry ?? null);
      if (cancelled) return;
      if (!name) return;
      form.setFieldValue('destination_port', name);
    })();
    return () => {
      cancelled = true;
    };
  }, [destCountry, destLocCode, fetchLocationNameByCode, form, readOnly]);

  const applyInitialToForm = React.useCallback(
    (s: MatLogisticShipment) => {
      hydratingRef.current = true;

      depPortTouchedRef.current = false;
      destPortTouchedRef.current = false;

      setUploadPath(String((s as any)?.upload_path || ''));

      const plannedFrom = s.planned_departure_date ? dayjs(s.planned_departure_date) : null;
      const plannedTo = s.planned_arrival_date ? dayjs(s.planned_arrival_date) : null;
      const plannedRange = plannedFrom || plannedTo ? ([plannedFrom, plannedTo] as [Dayjs | null, Dayjs | null]) : undefined;

      prevDepCountryRef.current = s.departure_country_code;
      prevDestCountryRef.current = s.destination_country_code;
      prevTransportTypeRef.current = (s.transport_type as string) ?? undefined;

      form.setFieldsValue({
        shipment_id: s.shipment_id,
        shipment_type: (s.shipment_type as ShipmentType) ?? 'direct',
        transport_type: (s.transport_type as TransportType) ?? 'tbd',
        po_number: s.po_number ?? null,
        invoice_no: s.invoice_no ?? null,
        departure_country_code: s.departure_country_code,
        departure_loc_code: s.departure_loc_code,
        departure_port: s.departure_port ?? null,
        destination_country_code: s.destination_country_code,
        destination_loc_code: s.destination_loc_code,
        destination_port: s.destination_port ?? null,
        planned_date_range: plannedRange,
        actual_departure_date: s.actual_departure_date ? dayjs(s.actual_departure_date) : null,
        actual_arrival_date: s.actual_arrival_date ? dayjs(s.actual_arrival_date) : null,
        forwarder_name: s.forwarder_name ?? null,
        carrier_name: s.carrier_name ?? null,
        vessel_name: s.vessel_name ?? null,
        container_no: s.container_no ?? null,
        airwaybill_no: s.airwaybill_no ?? null,
        billofLading_no: s.billofLading_no ?? null,
        plate_no1: s.plate_no1 ?? null,
        plate_no2: s.plate_no2 ?? null,
        vagon_no: s.vagon_no ?? null,
      });

      // ensure the saved location codes appear in Select even if not in first 200 options
      void (async () => {
        try {
          const depOpt = s.departure_loc_code ? await fetchLocationOptionByCode(String(s.departure_loc_code), s.departure_country_code) : null;
          if (depOpt) setDepLocOptions((prev) => mergeOptionFirst(depOpt, prev || []));
          const destOpt = s.destination_loc_code ? await fetchLocationOptionByCode(String(s.destination_loc_code), s.destination_country_code) : null;
          if (destOpt) setDestLocOptions((prev) => mergeOptionFirst(destOpt, prev || []));
        } catch {
          // ignore (best-effort)
        }
      })();

      // release hydration after effects have a chance to observe the hydrated values
      setTimeout(() => {
        hydratingRef.current = false;
      }, 0);
    },
    [fetchLocationOptionByCode, form, mergeOptionFirst]
  );

  React.useEffect(() => {
    if (mode !== 'edit') return;
    if (!initialShipment) return;
    applyInitialToForm(initialShipment);
  }, [applyInitialToForm, initialShipment, mode]);

  const onSubmit = async () => {
    try {
      if (readOnly) {
        message.info('Cancelled shipment is read-only');
        return;
      }
      const v = await form.validateFields();
      const plannedRange = v.planned_date_range as [Dayjs | null, Dayjs | null] | undefined;
      setSubmitting(true);
      const shipmentId = String(v.shipment_id);

      // Finalize file uploads (edit: before update; create: after insert below)
      let nextUploadPath: string | null | undefined = undefined;
      if (mode === 'edit') {
        if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
          const up = await fileUploadRef.current.finalizeUpload(shipmentId);
          if (!up.success) {
            message.error(up.error || 'File upload failed. Changes not saved.');
            return;
          }
          nextUploadPath = up.folderPath || null;
        }
      }

      const payload = {
        shipment_id: shipmentId,
        po_number: v.po_number ?? null,
        invoice_no: v.invoice_no ?? null,
        shipment_type: v.shipment_type ? String(v.shipment_type) : null,
        transport_type: v.transport_type ? String(v.transport_type) : null,
        departure_country_code: String(v.departure_country_code),
        departure_loc_code: String(v.departure_loc_code),
        departure_port: String(v.departure_port),
        destination_country_code: String(v.destination_country_code),
        destination_loc_code: String(v.destination_loc_code),
        destination_port: String(v.destination_port),
        planned_departure_date: plannedRange?.[0] ? plannedRange[0].format('YYYY-MM-DD') : null,
        actual_departure_date: v.actual_departure_date ? (v.actual_departure_date as Dayjs).format('YYYY-MM-DD') : null,
        planned_arrival_date: plannedRange?.[1] ? plannedRange[1].format('YYYY-MM-DD') : null,
        actual_arrival_date: v.actual_arrival_date ? (v.actual_arrival_date as Dayjs).format('YYYY-MM-DD') : null,
        forwarder_name: v.forwarder_name ?? null,
        carrier_name: v.carrier_name ?? null,
        vessel_name: v.vessel_name ?? null,
        container_no: v.container_no ?? null,
        airwaybill_no: v.airwaybill_no ?? null,
        billofLading_no: v.billofLading_no ?? null,
        plate_no1: v.plate_no1 ?? null,
        plate_no2: v.plate_no2 ?? null,
        vagon_no: v.vagon_no ?? null,
        upload_path: mode === 'edit' ? (nextUploadPath !== undefined ? nextUploadPath : ((initialShipment as any)?.upload_path ?? null)) : null,
      };
      if (mode === 'create') {
        await insertData('mat_logistic_shipments', payload);

        if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
          const up = await fileUploadRef.current.finalizeUpload(shipmentId);
          if (!up.success) {
            message.error(up.error || 'File upload failed');
          } else {
            const p = up.folderPath || null;
            await updateData('mat_logistic_shipments', shipmentId, { upload_path: p }, 'shipment_id');
            setUploadPath(p || '');
          }
        }

        message.success('Shipment created');
        form.resetFields();
        setUploadPath('');
      } else {
        const { shipment_id, ...updatePayload } = payload;
        await updateData('mat_logistic_shipments', shipmentId, updatePayload, 'shipment_id');
        if (nextUploadPath !== undefined) {
          setUploadPath(nextUploadPath || '');
        }
        message.success('Shipment updated');
      }
      onSuccess?.(shipmentId);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || (mode === 'edit' ? 'Failed to update shipment' : 'Failed to create shipment'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card style={{ margin: 8 }}>
      <Title level={3} style={{ margin: 12 }}>{mode === 'edit' ? 'Edit Shipment' : 'Create Shipment'}</Title>
      {readOnly && (
        <Alert
          type="warning"
          showIcon
          style={{ margin: '0 12px 12px 12px' }}
          message="This shipment is cancelled and cannot be edited."
          description={
            <div>
              <div><b>Reason:</b> {String((initialShipment as any)?.cancel_reason ?? '-')}</div>
              <div><b>Cancelled at:</b> {String((initialShipment as any)?.cancelled_at ?? '-')}</div>
              <div><b>Cancelled by:</b> {String((initialShipment as any)?.cancelled_by ?? '-')}</div>
            </div>
          }
        />
      )}
      <Form
        form={form}
        layout="horizontal"
        colon={false}
        labelAlign="left"
        labelCol={{ flex: '120px' }}
        wrapperCol={{ flex: 'auto' }}
        style={{ maxWidth: 1200, marginTop: 6 }}
      >
        <Row gutter={[12, 4]} style={{ width: '100%' }}>
          <Col xs={24} sm={12} md={12} lg={4} xl={4}>
            <Form.Item
              {...headerItemProps}
              name="shipment_id"
              label="Shipment ID"
              rules={[{ required: true }]}
              style={{ marginBottom: 4 }}
              normalize={toUpperOrSame}
            >
              <Input maxLength={30} disabled={mode === 'edit' || readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={4} xl={4}>
            <Form.Item {...headerItemProps} name="shipment_type" label="Shipment Type" rules={[{ required: true }]} style={{ marginBottom: 4 }}>
              <Select disabled={readOnly} style={{ width: '100%' }} options={shipmentTypeOptions.map(v => ({ label: v, value: v }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={4} xl={4}>
            <Form.Item {...headerItemProps} name="transport_type" label="Transport" rules={[{ required: true }]} style={{ marginBottom: 4 }}>
              <Select disabled={readOnly} style={{ width: '100%' }} options={transportTypeOptions.map(v => ({ label: v, value: v }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={4} xl={4}>
            <Form.Item {...headerItemProps} name="po_number" label="PO Number" style={{ marginBottom: 4 }} normalize={toUpperOrSame}>
              <Input maxLength={30} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={4} xl={4}>
            <Form.Item {...headerItemProps} name="invoice_no" label="Invoice No" style={{ marginBottom: 4 }} normalize={toUpperOrSame}>
              <Input maxLength={50} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={4} xl={4}>
            <Form.Item {...headerItemProps} label=" " style={{ marginBottom: 4 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                {!readOnly && (
                  <>
                    <Button
                      onClick={() => {
                        if (mode === 'edit' && initialShipment) {
                          applyInitialToForm(initialShipment);
                          return;
                        }
                        form.resetFields();
                      }}
                      disabled={submitting}
                    >
                      Reset
                    </Button>
                    <Button action={mode === 'edit' ? 'edit' : 'add'} type="primary" onClick={onSubmit} loading={submitting}>
                      {mode === 'edit' ? 'Update' : 'Create'}
                    </Button>
                  </>
                )}
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[8, 8]} style={{ width: '100%' }}>
          <Col xs={24} md={8}>
            <Card size="small" title="Departure" bodyStyle={{ padding: 8 }} style={{ background: '#e6f4ff' }}>
              <Form.Item name="departure_country_code" label="Country" rules={[{ required: true }]} style={itemStyle}>
                <Select
                  disabled={readOnly}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  options={countryOptions}
                  placeholder="Select country"
                  onClear={onClearDepartureCountry}
                />
              </Form.Item>
              <Form.Item name="departure_loc_code" label="Location" rules={[{ required: true }]} style={itemStyle}>
                <Select
                  disabled={readOnly}
                  allowClear
                  showSearch
                  onSearch={setDepSearch as any}
                  onSelect={clearDepSearch}
                  onBlur={clearDepSearch}
                  onClear={onClearDepartureLocation}
                  filterOption={false}
                  searchValue={depSearch}
                  style={{ width: '100%' }}
                  loading={loadingDep}
                  options={depLocOptions}
                  placeholder="Type to search location"
                />
              </Form.Item>
              <Form.Item name="departure_port" label="Port/Terminal" rules={[{ required: false }]} style={itemStyle} normalize={toUpperOrSame}>
                <Input
                  maxLength={50}
                  disabled={readOnly}
                  onChange={() => {
                    depPortTouchedRef.current = true;
                  }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card size="small" title="Destination" bodyStyle={{ padding: 8 }} style={{ background: '#f6ffed' }}>
              <Form.Item name="destination_country_code" label="Country" rules={[{ required: true }]} style={itemStyle}>
                <Select
                  disabled={readOnly}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  options={countryOptions}
                  placeholder="Select country"
                  onClear={onClearDestinationCountry}
                />
              </Form.Item>
              <Form.Item name="destination_loc_code" label="Location" rules={[{ required: true }]} style={itemStyle}>
                <Select
                  disabled={readOnly}
                  allowClear
                  showSearch
                  onSearch={setDestSearch as any}
                  onSelect={clearDestSearch}
                  onBlur={clearDestSearch}
                  onClear={onClearDestinationLocation}
                  filterOption={false}
                  searchValue={destSearch}
                  style={{ width: '100%' }}
                  loading={loadingDest}
                  options={destLocOptions}
                  placeholder="Type to search location"
                />
              </Form.Item>
              <Form.Item name="destination_port" label="Port/Terminal" rules={[{ required: false }]} style={itemStyle} normalize={toUpperOrSame}>
                <Input
                  maxLength={50}
                  disabled={readOnly}
                  onChange={() => {
                    destPortTouchedRef.current = true;
                  }}
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card size="small" title="Dates" bodyStyle={{ padding: 8 }} style={{ background: '#fffbe6' }}>
              <Form.Item
                name="planned_date_range"
                label="Planned"
                labelCol={{ flex: '80px', style: { whiteSpace: 'nowrap' } }}
                wrapperCol={{ flex: 'auto' }}
                style={{ ...itemStyle }}
              >
                <DatePicker.RangePicker disabled={readOnly} style={{ width: '85%' }} />
              </Form.Item>
              <Form.Item
                name="actual_departure_date"
                label="Actual Departure"
                labelCol={{ flex: '130px', style: { whiteSpace: 'nowrap' } }}
                wrapperCol={{ flex: 'auto' }}
                style={{ ...itemStyle }}
              >
                <DatePicker disabled={readOnly} style={{ width: '80%' }} />
              </Form.Item>
              <Form.Item
                name="actual_arrival_date"
                label="Actual Arrival"
                labelCol={{ flex: '130px', style: { whiteSpace: 'nowrap' } }}
                wrapperCol={{ flex: 'auto' }}
                style={{ ...itemStyle }}
              >
                <DatePicker disabled={readOnly} style={{ width: '80%' }} />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Space wrap style={{ width: '100%', marginTop: 8 }} size={[8, 8]}>
          <Form.Item name="forwarder_name" label="Forwarder" style={{ minWidth: 260, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={100} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="carrier_name" label="Carrier" style={{ minWidth: 260, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={100} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="vessel_name" label="Vessel Name" style={{ minWidth: 220, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={30} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="container_no" label="Container No" style={{ minWidth: 200, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={30} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="airwaybill_no" label="Air Waybill No" style={{ minWidth: 200, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={30} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="billofLading_no" label="Bill of Lading No" style={{ minWidth: 220, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={30} disabled={readOnly} />
          </Form.Item>
        </Space>

        <Space wrap style={{ width: '100%', marginTop:8 }} size={[8, 8]}>
          <Form.Item name="plate_no1" label="Plate No 1" style={{ minWidth: 160, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={20} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="plate_no2" label="Plate No 2" style={{ minWidth: 160, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={20} disabled={readOnly} />
          </Form.Item>
          <Form.Item name="vagon_no" label="Vagon No" style={{ minWidth: 160, ...itemStyle }} normalize={toUpperOrSame}>
            <Input maxLength={20} disabled={readOnly} />
          </Form.Item>
        </Space>

        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <Space wrap>
            {!readOnly && <FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />}
            {uploadPath && uploadPath.trim() !== '' && (
              <FileDownloadTool
                buttonText={(<Button type="primary" style={{ background: '#52c41a', borderColor: '#52c41a' }}>Attached Files</Button>)}
                contentPath={uploadPath}
              />
            )}
          </Space>
        </div>
      </Form>
    </Card>
  );
};

const MatLogShipmentCreate: React.FC = () => {
  return <MatLogShipmentUpsert mode="create" />;
};

export default MatLogShipmentCreate;
